/* "Little surprises" (owner request 2026-10-13), the pure part: the 7-tap and keyboard-code detectors, milestone cheers,
   the perfect hour, the night owl and the owner's daily line. No screen, no storage. Tested in tests/app/surprises.test.ts.
   (Never names the old time-tracking product.) */
import {DAY_STRIP_FROM, DAY_STRIP_TO} from './time.ts';

/** n taps within `within` ms (e.g. the logo tapped 7 times in 3 seconds): feed each tap's time; true on the n-th, then it starts over */
export function tapCounter(n = 7, within = 3000) {
  let taps: number[] = [];
  return (t: number): boolean => {
    taps = taps.filter(x => t - x <= within); taps.push(t);
    if (taps.length >= n) { taps = []; return true; }
    return false;
  };
}

/** the keyboard code ↑ ↑ ↓ ↓ ← → ← → B A: feed each KeyboardEvent.key; true when the last ten keys were exactly the code */
export const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
export function konamiDetector() {
  let last: string[] = [];
  return (key: string): boolean => {
    last.push(key.length === 1 ? key.toLowerCase() : key);
    if (last.length > KONAMI.length) last = last.slice(-KONAMI.length);
    if (last.length === KONAMI.length && last.every((k, i) => k === KONAMI[i])) { last = []; return true; }
    return false;
  };
}

/** the hours (all time, the person's own) worth a quiet cheer */
export const MILESTONES = [1000, 5000, 10000, 25000];
/** the highest milestone reached with this many hours (0: none yet) */
export const milestoneLevel = (hours: number) => MILESTONES.filter(m => hours >= m).pop() || 0;
/** stored: the milestone already noted on this device for this person (null: never checked here: the first run notes the
    current level silently, so milestones passed before this feature are never cheered). Returns what to store and the
    milestone to cheer now (null: nothing). */
export function milestoneStep(stored: number | null, hours: number): {store: number; cheer: number | null} {
  const level = milestoneLevel(hours);
  if (stored === null) return {store: level, cheer: null};
  return level > stored ? {store: level, cheer: level} : {store: stored, cheer: null};
}
export const milestoneText = (m: number) => `🎉 You just passed ${m.toLocaleString('en-US')} hours. That's a lot of good work.`;

/** stopped at exactly 1:00:00 */
export const isPerfectHour = (durationSeconds: number) => durationSeconds === 3600;
export const PERFECT_HOUR = 'Perfect hour. Show-off. 😎';

/** the night owl (owner request 2026-11-02): while a timer runs and it is now outside the day strip of the Timeline skins
    (lib/time.ts DAY_STRIP_FROM-DAY_STRIP_TO: 6 AM-10 PM), an owl sits beside RUNNING; from 1:00 to 4:59 AM a sleepy one.
    wall = now, 'YYYY-MM-DDTHH:MM:SS' in the person's own zone. '' = no owl. */
export type OwlKind = '' | 'owl' | 'sleepy';
export const SLEEPY_FROM = 1 * 60, SLEEPY_TO = 5 * 60;
export function owlAt(wall: string): OwlKind {
  const t = /T(\d{2}):(\d{2})/.exec(wall || '');
  if (!t) return '';
  const min = +t[1] * 60 + +t[2];
  if (min >= DAY_STRIP_FROM && min < DAY_STRIP_TO) return '';
  return min >= SLEEPY_FROM && min < SLEEPY_TO ? 'sleepy' : 'owl';
}
export const OWL_LABEL = 'Night owl 🦉', SLEEPY_LABEL = 'Still up? Even the owl is yawning.';

/** the owner's line at the bottom of Settings: one a day, in turn */
export const OWNER_LINES = [
  "Time is the only inventory you can't restock.",
  'Hours well kept are hours well spent.',
  'Small hours add up to big work.',
  "A good day's work leaves a good record.",
  'The clock keeps time. You make it count.',
  'Every entry is a small promise kept.',
  'What gets measured gets understood.',
  'Busy is a feeling. Hours are a fact.',
  'The best time to log it was when it happened. The second best is now.',
  'Good records make for easy Fridays.',
  'Work expands to fill the time. Records keep it honest.',
  'Ten minutes saved a day is a week saved a year.',
  "You can't make more time, only better use of it.",
  'Steady beats busy.',
  'Clients buy outcomes. The hours tell the story.',
  'A minute logged is a minute remembered.',
  "Today's hours are tomorrow's history.",
  'Nobody ever wished for messier timesheets.',
  'Quiet work, well kept.',
  'The work is the thing. The record is the proof.',
];
/** only the owner sees it ('' for everyone else); date = the owner's own day 'YYYY-MM-DD' */
export function ownerLine(role: string | null | undefined, date: string): string {
  if (role !== 'owner' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const day = Math.floor(Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10)) / 86400000);
  return OWNER_LINES[day % OWNER_LINES.length];
}

/* ---------- the day's holiday effect (owner 2026-10-31: "see the leaves fall again"): it plays on every load of the app,
   when the logo is tapped, and now and then while the app is open. Nothing is stored. ---------- */
/** the random wait between two unasked-for effects: 45 to 120 minutes (r: a random number 0-1) */
export const FX_GAP_MIN = 45 * 60_000, FX_GAP_MAX = 120 * 60_000;
export const fxGap = (r = Math.random()) => Math.round(FX_GAP_MIN + Math.min(1, Math.max(0, r)) * (FX_GAP_MAX - FX_GAP_MIN));
/** a busy moment (a window open, typing, the full-screen timer, the page hidden): look again this much later */
export const FX_RETRY = 2 * 60_000;
/** logo taps replay the effect: never while one is still on screen, and at most once every `gap` ms (the 7 quick taps of
    the about card start it at most once). Feed the tap's time and whether an effect is playing; true = play it now. */
export function replayGate(gap = 6000) {
  let last = -Infinity;
  return (t: number, playing: boolean): boolean => {
    if (playing || t - last < gap) return false;
    last = t; return true;
  };
}
/** is this a quiet moment for an effect nobody asked for? */
export const quietMoment = (m: {visible: boolean; windowOpen: boolean; typing: boolean; fullScreen: boolean}) =>
  m.visible && !m.windowOpen && !m.typing && !m.fullScreen;
